import * as XLSX from 'xlsx-js-style';

interface HeaderExportArgs {
  ident?: string;
  name?: string;
  workOrder?: string;
  validFrom?: string;
  validTo?: string;
  productionCode?: string;
  versionDisplay?: string;
  batch?: number | null;
  batchUnitCode?: string;
  allowedYieldValue?: string | number | null;
  allowedWasteValue?: string | number | null;
  statusText?: string; // Статус — already-translated MK labels, joined
  preparedName?: string; // Подготвил
  preparedDate?: string; // Датум подготвил (DD.MM.YYYY, written through as-is)
  approvedName?: string; // Одобрил
  approvedDate?: string; // Датум одобрил (DD.MM.YYYY, written through as-is)
  subprocessesText?: string; // Подпроцеси — names joined, in display order
}

const headerStyle = {
  fill: { fgColor: { rgb: 'D9D9D9' } },
  font: { bold: true, color: { rgb: '000000' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: '999999' } },
    bottom: { style: 'thin', color: { rgb: '999999' } },
    left: { style: 'thin', color: { rgb: '999999' } },
    right: { style: 'thin', color: { rgb: '999999' } },
  },
};

const cellStyle = {
  alignment: { vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: 'DDDDDD' } },
    bottom: { style: 'thin', color: { rgb: 'DDDDDD' } },
    left: { style: 'thin', color: { rgb: 'DDDDDD' } },
    right: { style: 'thin', color: { rgb: 'DDDDDD' } },
  },
};

const safeFilenamePart = (value?: string) =>
  String(value || 'normativ')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80);

// 0 is a valid numeric value and must be written as 0 — only a genuinely absent
// value (null/undefined) collapses to a blank cell.
const numOrBlank = (value: number | null | undefined): number | string =>
  value === null || value === undefined ? '' : value;

export function downloadOSHeaderExcel({
  ident = '',
  name = '',
  workOrder = '',
  validFrom = '',
  validTo = '',
  productionCode = '',
  versionDisplay = '',
  batch = null,
  batchUnitCode = '',
  allowedYieldValue = null,
  allowedWasteValue = null,
  statusText = '',
  preparedName = '',
  preparedDate = '',
  approvedName = '',
  approvedDate = '',
  subprocessesText = '',
}: HeaderExportArgs) {
  const data: Record<string, string | number>[] = [
    {
      'Иденти': ident,
      'Назив': name,
      'Количина на серија': numOrBlank(batch),
      'ЕМ': batchUnitCode,
      'Дозволен принос (ЕМ)': allowedYieldValue ?? '',
      'Дозволен отпад (%)': allowedWasteValue ?? '',
      'Тип на РН': workOrder,
      'Шифра на Тех. Пос.': productionCode,
      'Верзија': versionDisplay,
      'Важи од': validFrom,
      'Важи до': validTo,
      'Подготвил': preparedName,
      'Одобрил': approvedName,
      'Статус': statusText,
      'Датум подготвил': preparedDate,
      'Датум одобрил': approvedDate,
      'Подпроцеси': subprocessesText,
    },
  ];

  const headers = [
    'Иденти',
    'Назив',
    'Количина на серија',
    'ЕМ',
    'Дозволен принос (ЕМ)',
    'Дозволен отпад (%)',
    'Тип на РН',
    'Шифра на Тех. Пос.',
    'Верзија',
    'Важи од',
    'Важи до',
    'Подготвил',
    'Одобрил',
    'Статус',
    'Датум подготвил',
    'Датум одобрил',
    'Подпроцеси',
  ];

  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
  // Widths follow the `headers` order 1:1 — each width is carried with its column.
  worksheet['!cols'] = [
    { wch: 16 }, // Иденти
    { wch: 16 }, // Назив
    { wch: 18 }, // Количина на серија
    { wch: 10 }, // ЕМ
    { wch: 18 }, // Дозволен принос (ЕМ)
    { wch: 16 }, // Дозволен отпад (%)
    { wch: 14 }, // Тип на РН
    { wch: 10 }, // Шифра на Тех. Пос.
    { wch: 10 }, // Верзија
    { wch: 14 }, // Важи од
    { wch: 14 }, // Важи до
    { wch: 32 }, // Подготвил
    { wch: 14 }, // Одобрил
    { wch: 24 }, // Статус
    { wch: 20 }, // Датум подготвил
    { wch: 20 }, // Датум одобрил
    { wch: 32 }, // Подпроцеси
  ];
  worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(XLSX.utils.decode_range(worksheet['!ref'] || 'A1:Q1')) };

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:Q1');
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
    if (worksheet[cellRef]) worksheet[cellRef].s = headerStyle;
  }
  for (let r = 1; r <= range.e.r; r += 1) {
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (worksheet[cellRef]) worksheet[cellRef].s = cellStyle;
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Заглавие');
  XLSX.writeFile(
    workbook,
    `normativ_zaglavie_${safeFilenamePart(ident)}_${new Date().toISOString().split('T')[0]}.xlsx`
  );
}
